/**
 * 共通処理です
 * @class util.ts
 * @constructor
 */
import { useDispatch } from "react-redux";
import { setIsDeviceType } from "../features/userSlice";

//=============================================================================
//  制御、その他
//=============================================================================
/**
 * 別タブ遷移
 * @param url : url文字列
 */
export const openWebSite = (url: string) => {
  window.open(url, "_blank", "noreferrer noopener");
};

// デバイス・OSの判定を行う
export const useDeviceType = () => {
  const ua = navigator.userAgent;
  const loerUa = navigator.userAgent.toLowerCase();
  const dispatch = useDispatch();
  if (ua.indexOf("iPhone") > -1) {
    dispatch(
      setIsDeviceType({
        deviceType: true,
        ios: true,
        iphone: true,
      })
    );
  } else if (ua.indexOf("Android") > -1 && ua.indexOf("Mobile") > -1) {
    dispatch(
      setIsDeviceType({
        deviceType: true,
        ios: false,
        iphone: false,
      })
    );
  } else if (ua.indexOf("iPad") > -1) {
    dispatch(
      setIsDeviceType({
        deviceType: true,
        ios: true,
        iphone: false,
      })
    );
  } else if (/ipad|macintosh/.test(loerUa) && "ontouchend" in document) {
    // OS判定がMacOSX向けになっているもの
    dispatch(
      setIsDeviceType({
        deviceType: true,
        ios: true,
        iphone: false,
      })
    );
  } else if (ua.indexOf("Android") > -1) {
    dispatch(
      setIsDeviceType({
        deviceType: true,
        ios: false,
        iphone: false,
      })
    );
  } else {
    dispatch(
      setIsDeviceType({
        deviceType: false,
        ios: false,
        iphone: false,
      })
    );
  }
};

//=============================================================================
//  時間・日付
//=============================================================================
/**
 * UnixTimeの数値を返す
 * @returns {number} UNIXTIME  例:1639381897
 */
export const getUnixTime = (): number => {
  // Dateオブジェクトを作成し、UNIXタイムスタンプを取得する (ミリ秒単位)
  const unixTimestamp = new Date().getTime();
  // UNIXタイムスタンプを取得する (秒単位 - PHPのtime()と同じ)
  return Math.floor(unixTimestamp / 1000);
};

export const getNowYMD = (): string => {
  var dt = new Date();
  var y = dt.getFullYear();
  var m = ("00" + (dt.getMonth() + 1)).slice(-2);
  var d = ("00" + dt.getDate()).slice(-2);
  var result = y + m + d;
  return result;
};

/**
 * 現在のJST日時を取得（API用のフォーマット）
 * @param {string} separator - 日付の区切り文字（デフォルト: '/'）
 * @returns {string} JST日時文字列 例: "2025/08/22 00:00:00" または "2025-08-22 00:00:00"
 */
export const getJSTDateTimeForAPI = (separator: string = '/'): string => {
  const now = new Date();
  const jstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
  const year = jstDate.getFullYear();
  const month = String(jstDate.getMonth() + 1).padStart(2, '0');
  const day = String(jstDate.getDate()).padStart(2, '0');
  const hours = String(jstDate.getHours()).padStart(2, '0');
  const minutes = String(jstDate.getMinutes()).padStart(2, '0');
  const seconds = String(jstDate.getSeconds()).padStart(2, '0');
  
  return `${year}${separator}${month}${separator}${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 現在の週の月曜日をYYYYMMDD形式で取得
 * @returns {string} 月曜日の日付 例: "20250804"
 */
export const getCurrentWeekMonday = (): string => {
  const now = new Date();
  const jstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
  
  // 現在の曜日を取得（0:日曜日 ~ 6:土曜日）
  const dayOfWeek = jstDate.getDay();
  
  // 月曜日までの日数を計算（日曜日の場合は-6、それ以外は1-dayOfWeek）
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  // 月曜日の日付を計算
  const monday = new Date(jstDate);
  monday.setDate(jstDate.getDate() + daysToMonday);
  
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const day = String(monday.getDate()).padStart(2, '0');
  
  return `${year}${month}${day}`;
};

/**
 * 過去N週分の月曜日の日付リストを取得
 * @param {number} weeks - 取得する週数（デフォルト: 5）
 * @returns {Array<{value: string, label: string}>} 月曜日の日付リスト
 */
export const getPastWeekMondays = (weeks: number = 5): Array<{value: string, label: string}> => {
  const mondayList: Array<{value: string, label: string}> = [];
  const now = new Date();
  const jstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
  
  // 現在の曜日を取得（0:日曜日 ~ 6:土曜日）
  const dayOfWeek = jstDate.getDay();
  
  // 今週の月曜日までの日数を計算
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  // 今週の月曜日の日付を計算
  const currentMonday = new Date(jstDate);
  currentMonday.setDate(jstDate.getDate() + daysToMonday);
  
  // 過去N週分の月曜日を生成
  for (let i = 0; i < weeks; i++) {
    const monday = new Date(currentMonday);
    monday.setDate(currentMonday.getDate() - (i * 7));
    
    const year = monday.getFullYear();
    const month = String(monday.getMonth() + 1).padStart(2, '0');
    const day = String(monday.getDate()).padStart(2, '0');
    
    const value = `${year}${month}${day}`;
    const label = `${year}/${month}/${day}`;
    
    mondayList.push({ value, label });
  }
  
  return mondayList;
};

// 操作イベント一覧
export const eventListeners = [
  "scroll",
  "resize",
  "click",
  "contextmenu",
  "wheel",
  "keypress",
  "mousedown",
  "mousemove",
  "touchstart",
  "touchmove",
  "touchend",
  "touchend",
];

export const DAY_TIMER_SECOND = 86400; // 24時間の秒数
export const HOUR_TIMER_SECOND = 3600; // 1時間の秒数
export const HOUR_TIMER_MILL_SECOND = 3600000; // 1時間のミリ秒数

// ユーザの参照権限チェック
export const checkAuthUser = (user: any) => {
  let userInfo = user;
  // 参照権限のないユーザーをリダイレクト（URL直遷移対策）
  if (process.env.NODE_ENV !== "development") {
    if (!userInfo.apps?.includes("tenpo-check")) {
      window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
    }
  }
};

// ログイン時間チェック
export const checkOperationCount = (count: number) => {
  if (process.env.NODE_ENV !== "development") {
    if (count === 0) {
      window.location.href = process.env.REACT_APP_DIGITAL_TOOL!;
      return false;
    }
  }
  return true;
};
